import { formatISODate } from "../../../utils/datetime";
import { paginate, PaginatedResult } from "../../components/Pagination/utils";
import {
  AnalyticsDimension,
  AnalyticsReportQuery,
  MetricName,
  ReportConfig,
  useAnalyticsReportQuery,
  useAnalyticsReportTrendsQuery,
} from "../../graphql";
import useFeatureFlag from "../../graphql/hooks/useFeatureFlag";
import {
  AnalyticsConfig,
  CommonQueryVariables,
  LineChartDataPoint,
} from "./types";
import useAnalyticsContext from "./useAnalyticsContext";
import useReportTableData from "./useReportTableData";
import { getAverageScore, getLineChartData, sortTableData } from "./utils";

export type ReportData = {
  report?: AnalyticsReportQuery["qualityReport"];
  pagination: PaginatedResult;
  loading: boolean;
  error?: any;
  commonQueryVariables: CommonQueryVariables;
  filtersAvailable: boolean;
  chart: {
    chartData: LineChartDataPoint[];
    columnsConfig: ReportConfig[];
    averageScore: number | null;
    comparisonAverageScore: number | null;
    totalCalls: number;
    numInterviewers: number;
    loading: boolean;
    error?: any;
    bucketSize?: number;
    bucketInterval?: string;
  };
  refetchAllData: () => void;
};

/**
 * Query results and any error states related to quality report.
 */
const useReportData = (reportConfig: AnalyticsConfig): ReportData => {
  const trendsEnabled = useFeatureFlag("analytics:quality-report-trends");
  const { selectedOrgId } = useAnalyticsContext();

  // table data with comparison data
  const { report, comparisonReport } = useReportTableData({
    dateRangeStart: reportConfig.dateRange.value.start,
    dateRangeEnd: reportConfig.dateRange.value.end,
    additionalQueryVariables: {
      primaryDimension: reportConfig.primaryDimension.value,
      secondaryDimension: reportConfig.secondaryDimension.value,
      requestedOrganizationId: selectedOrgId,
      ...reportConfig.filters,
    },
  });
  const dataPoints = report.report?.data || [];
  const sortedDataPoints = sortTableData(
    dataPoints,
    reportConfig.report.sortColumn.value,
    reportConfig.report.sortDirection.value
  );

  let pageLimit = reportConfig.chartLimit.value;
  if (pageLimit === -1) {
    pageLimit = dataPoints.length;
  }

  const commonQueryVariables: CommonQueryVariables = {
    metric: MetricName.Report,
    dateRangeStart: formatISODate(reportConfig.dateRange.value.start),
    dateRangeEnd: formatISODate(reportConfig.dateRange.value.end),
    organizationId: selectedOrgId,
    ...reportConfig.filters,
  };
  const reportTrendsDataQuery = useAnalyticsReportTrendsQuery({
    notifyOnNetworkStatusChange: true,
    variables: {
      ...commonQueryVariables,
      requestedOrganizationId: selectedOrgId,
    },
    skip: !trendsEnabled,
  });

  const baseData = reportTrendsDataQuery.data?.qualityReportTrends?.data || [];
  const comparisonData =
    reportTrendsDataQuery.data?.qualityReportTrends?.comparisonData || [];
  const columnsConfig =
    reportTrendsDataQuery.data?.qualityReportTrends?.config || [];
  const { bucketSize, bucketInterval } =
    reportTrendsDataQuery.data?.qualityReportTrends || {};

  const interviewersQuery = useAnalyticsReportQuery({
    notifyOnNetworkStatusChange: true,
    variables: {
      ...commonQueryVariables,
      primaryDimension: AnalyticsDimension.Interviewer,
      secondaryDimension: AnalyticsDimension.None,
      requestedOrganizationId: selectedOrgId,
    },
  });
  const numInterviewers =
    interviewersQuery.data?.qualityReport?.data?.length || 0;

  const refetchAllData = (): void => {
    report.refetch();
    comparisonReport.refetch();
    reportTrendsDataQuery.refetch();
    interviewersQuery.refetch();
  };

  return {
    report: report.report,
    pagination: paginate(
      sortedDataPoints,
      reportConfig.report.page.value,
      pageLimit
    ),
    loading: report.loading || comparisonReport.loading,
    error: report.error || comparisonReport.error,
    commonQueryVariables,
    filtersAvailable: true,
    chart: {
      chartData: getLineChartData(baseData, comparisonData),
      columnsConfig,
      averageScore: getAverageScore(report.report?.data || []),
      comparisonAverageScore: getAverageScore(
        comparisonReport.report?.data || []
      ),
      totalCalls: baseData.reduce((acc: number, d) => {
        const calls: number = d.totalCalls || 0;
        return acc + calls;
      }, 0),
      numInterviewers,
      bucketSize: bucketSize || 0,
      bucketInterval: bucketInterval || "",
      loading: reportTrendsDataQuery.loading || interviewersQuery.loading,
      error: reportTrendsDataQuery.error || interviewersQuery.error,
    },
    refetchAllData,
  };
};

export default useReportData;
